import axios from 'axios'
import config from '../config'

// Create a public API instance without authentication
const publicApi = axios.create({
  baseURL: config.api.endpoint,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Get public company data including bookings, job types, reviews, and FAQs
 * @param {number} companyId - The company ID
 * @returns {Promise} Company public data
 */
export const getCompanyPublicData = async (
  companyId,
  previewToken = config.previewToken
) => {
  try {
    const params = previewToken ? { preview_token: previewToken } : {}
    const response = await publicApi.get(`/core/company/${companyId}/public/`, {
      params,
    })
    return response.data
  } catch (error) {
    console.error('Error fetching company data:', error)
    throw error
  }
}

/**
 * Look up company by slug
 * Used when company ID is not available (e.g., accessed via public URL)
 * @param {string} slug - The company slug
 * @param {string} previewToken - Optional preview token for disabled sites
 * @returns {Promise} Company lookup result with company_id and access info
 */
export const getCompanyBySlug = async (slug, previewToken = null) => {
  try {
    const params = previewToken ? { preview_token: previewToken } : {}
    const response = await publicApi.get(`/core/company/by-slug/${slug}/`, { params })
    return response.data
  } catch (error) {
    console.error('Error looking up company by slug:', error)
    throw error
  }
}

/**
 * Search for group bookings by postcode and optional job type
 * @param {number} companyId - The company ID
 * @param {string} postcode - The postcode to search
 * @param {number} jobTypeId - Optional job type ID to filter by
 * @returns {Promise} Search results with matching bookings
 */
export const searchGroupBookings = async (
  companyId,
  postcode,
  jobTypeId = null,
  previewToken = config.previewToken
) => {
  try {
    const params = {
      company_id: companyId,
      postcode: postcode.trim().toUpperCase(),
    }

    // Only add job_type_id if it's provided
    if (jobTypeId) {
      params.job_type_id = jobTypeId
    }

    if (previewToken) {
      params.preview_token = previewToken
    }

    const response = await publicApi.get('/core/group_booking/search/', {
      params,
    })
    return response.data
  } catch (error) {
    console.error('Error searching bookings:', error)
    throw error
  }
}

/**
 * Get unsubscribe info by token
 * @param {string} token - The unsubscribe token
 * @returns {Promise} Unsubscribe info (customer name, business name, already_unsubscribed)
 */
export const getUnsubscribeInfo = async token => {
  const response = await publicApi.get(`/unsubscribe/${token}/`)
  return response.data
}

/**
 * Confirm unsubscribe
 * @param {string} token - The unsubscribe token
 * @returns {Promise} Confirmation response
 */
export const confirmUnsubscribe = async token => {
  const response = await publicApi.post(`/unsubscribe/${token}/`)
  return response.data
}

/**
 * Verify a booking confirmation magic link
 * @param {string} token - The magic link token
 * @returns {Promise} Booking confirmation details
 */
export const verifyBooking = async token => {
  const response = await publicApi.get(`/verify-booking/${token}/`)
  return response.data
}

/**
 * Get cancel booking info
 * @param {string} token - The cancel booking token
 * @returns {Promise} Job details for cancellation confirmation
 */
export const getCancelBookingInfo = async token => {
  const response = await publicApi.get(`/cancel-booking/${token}/`)
  return response.data
}

/**
 * Confirm booking cancellation
 * @param {string} token - The cancel booking token
 * @returns {Promise} Cancellation confirmation
 */
export const confirmCancelBooking = async token => {
  const response = await publicApi.post(`/cancel-booking/${token}/`)
  return response.data
}

/**
 * Submit a B2C booking (public endpoint)
 * Creates a pending job and sends a confirmation email with magic link
 * @param {Object} bookingData - Booking submission data
 * @returns {Promise} Submission result with status
 */
export const submitB2CBooking = async bookingData => {
  const response = await publicApi.post('/public/b2c-booking/', bookingData)
  return response.data
}

export default publicApi
